import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, MessageCircle, Check, MapPin } from "lucide-react";
import { COMPANY, SERVICES, BAIRROS, bairroUrl, serviceUrl, waLink } from "@/data/site";
import { SEO } from "@/components/SEO";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { FAQAccordion } from "@/components/FAQAccordion";
import { CTABanner } from "@/components/CTABanner";

export default function ServicoDetail() {
  const { slug } = useParams();
  const cleanSlug = slug?.replace(/\.html$/, "");
  const service = SERVICES.find((s) => s.slug === cleanSlug);
  if (!service) return <Navigate to="/servicos.html" replace />;

  return (
    <>
      <SEO
        title={`${service.name} 24h em São Paulo | Desentupidora Paulistana`}
        description={`${service.short} Atendimento 24h nos bairros nobres de São Paulo. Ligue ${COMPANY.phoneDisplay}.`}
        path={`/servicos/${service.slug}`}
      />
      <section className="bg-brand-black text-white">
        <div className="max-w-7xl mx-auto container-px py-12 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Serviços", to: "/servicos.html" }, { label: service.name }]} />
            <span className="inline-block bg-brand-red text-white text-xs font-bold uppercase tracking-wide px-3 py-1 rounded mt-4 mb-3">{service.label}</span>
            <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl mb-4">{service.name}</h1>
            <p className="text-gray-300 mb-7">{service.intro}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={`tel:${COMPANY.phoneTel}`} className="btn-red"><Phone className="w-5 h-5" /> {COMPANY.phoneDisplay}</a>
              <a href={waLink(`Olá! Quero um orçamento de ${service.name}.`)} target="_blank" rel="noopener noreferrer" className="btn-green"><MessageCircle className="w-5 h-5" /> Chamar no WhatsApp</a>
            </div>
          </div>
          <img src={service.image} alt={service.name} className="rounded-xl w-full object-cover shadow-lg" />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-7xl mx-auto container-px py-16 grid lg:grid-cols-2 gap-12">
          <div>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-black mb-5">Por que escolher a Paulistana?</h2>
            <ul className="space-y-3">
              {service.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="grid place-items-center w-6 h-6 rounded-full bg-wa-green shrink-0 mt-0.5"><Check className="w-3.5 h-3.5 text-white" /></span>
                  <span className="text-gray-700">{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl text-brand-black mb-5">Perguntas frequentes</h2>
            <FAQAccordion items={service.faq} testIdPrefix={`service-faq`} />
          </div>
        </div>
      </section>

      {/* Serviço em todos os bairros */}
      <section className="bg-brand-light border-y border-gray-200">
        <div className="max-w-7xl mx-auto container-px py-14">
          <h2 className="font-display font-extrabold text-2xl text-brand-black mb-2">{service.name} nos bairros nobres</h2>
          <p className="text-gray-600 mb-6">Atendemos {service.name.toLowerCase()} em todas as regiões premium de São Paulo.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
            {BAIRROS.map((b) => (
              <Link key={b.slug} to={bairroUrl(b.slug)} className="flex items-center gap-2 text-sm text-brand-black hover:text-brand-red transition-colors bg-white rounded px-3 py-2 border border-gray-100">
                <MapPin className="w-3.5 h-3.5 text-brand-red shrink-0" /> {b.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Outros serviços */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto container-px py-14">
          <h2 className="font-display font-extrabold text-2xl text-brand-black mb-6">Outros serviços</h2>
          <div className="flex flex-wrap gap-3">
            {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
              <Link key={s.slug} to={serviceUrl(s.slug)} className="btn-dark text-sm !py-2.5">{s.name}</Link>
            ))}
          </div>
        </div>
      </section>

      <CTABanner />
    </>
  );
}
